import type { MeResponse, NavigationGroupDto, NavigationItemDto } from '@an-crm/shared';

/**
 * Super Admin console navigation. The API is expected to return this group for platform admins;
 * it is only used as a fallback if /me/navigation comes back without any /platform entries.
 */
export const PLATFORM_NAV_GROUP: NavigationGroupDto = {
  group: 'platform',
  label: 'Platform',
  items: [
    {
      key: 'platform-tenants',
      label: 'Tenants',
      path: '/platform/tenants',
      icon: 'building-2',
      moduleKey: 'platform',
      status: 'available',
    },
    {
      key: 'platform-plans',
      label: 'Plans',
      path: '/platform/plans',
      icon: 'layers',
      moduleKey: 'platform',
      status: 'available',
    },
    {
      key: 'platform-modules',
      label: 'Modules',
      path: '/platform/modules',
      icon: 'boxes',
      moduleKey: 'platform',
      status: 'available',
    },
    {
      key: 'platform-jobs',
      label: 'Jobs',
      path: '/platform/jobs',
      icon: 'calendar-clock',
      moduleKey: 'platform',
      status: 'available',
    },
  ],
};

/** Screens that live outside the module navigation (reached from the user menu or ⌘K). */
export const STANDALONE_PAGES: Readonly<Record<string, string>> = {
  '/account': 'Account',
};

export function isPlatformSession(me: MeResponse): boolean {
  return me.tenant === null && me.user.isPlatformAdmin;
}

/** Navigation exactly as served by the API, with the platform fallback for Super Admins. */
export function effectiveNavigation(nav: NavigationGroupDto[], me: MeResponse): NavigationGroupDto[] {
  const groups = nav.filter((g) => g.items.length > 0);
  if (!isPlatformSession(me)) return groups;
  const platformIndex = groups.findIndex((g) => g.items.some((i) => i.path.startsWith('/platform')));
  if (platformIndex === -1) return [PLATFORM_NAV_GROUP, ...groups];
  // the console screens exist in this build even if the API's nav predates them: add what's missing
  const group = groups[platformIndex] as NavigationGroupDto;
  const paths = new Set(groups.flatMap((g) => g.items.map((i) => i.path)));
  const missing = PLATFORM_NAV_GROUP.items.filter((i) => !paths.has(i.path));
  if (missing.length === 0) return groups;
  const merged = { ...group, items: [...group.items, ...missing] };
  return groups.map((g, i) => (i === platformIndex ? merged : g));
}

export interface NavMatch {
  group: NavigationGroupDto;
  item: NavigationItemDto;
}

/** Longest-prefix match of a pathname against nav item paths. */
export function findNavMatch(nav: NavigationGroupDto[], pathname: string): NavMatch | null {
  let best: NavMatch | null = null;
  for (const group of nav) {
    for (const item of group.items) {
      const exact = pathname === item.path;
      const prefix = pathname.startsWith(item.path.endsWith('/') ? item.path : `${item.path}/`);
      if ((exact || prefix) && (!best || item.path.length > best.item.path.length)) best = { group, item };
    }
  }
  return best;
}

export function flattenNav(nav: NavigationGroupDto[]): NavMatch[] {
  return nav.flatMap((group) => group.items.map((item) => ({ group, item })));
}

export interface Crumb {
  label: string;
  to?: string;
}

export function buildCrumbs(nav: NavigationGroupDto[], pathname: string, tail: string | null): Crumb[] {
  if (pathname === '/') return [];
  const standalone = STANDALONE_PAGES[pathname];
  if (standalone) return [{ label: standalone }];
  const match = findNavMatch(nav, pathname);
  if (!match) return [{ label: 'Not found' }];
  const crumbs: Crumb[] = [{ label: match.group.label }];
  const isDeeper = pathname !== match.item.path;
  crumbs.push(isDeeper ? { label: match.item.label, to: match.item.path } : { label: match.item.label });
  if (isDeeper) crumbs.push({ label: tail ?? 'Details' });
  return crumbs;
}

/** Label for the document title. */
export function pageTitleFor(nav: NavigationGroupDto[], pathname: string): string {
  if (pathname === '/') return 'Home';
  return STANDALONE_PAGES[pathname] ?? findNavMatch(nav, pathname)?.item.label ?? 'Not found';
}
